import ncaaSchoolColors from './ncaaSchoolColors.json';

export const UNIVERSITY_THEMES = {
  'Oregon State University': {
    primary: '#DC4405',
    secondary: '#000000',
    monogram: 'OSU',
    label: 'Beavers',
  },
  'University of Oregon': {
    primary: '#154733',
    secondary: '#FEE123',
    monogram: 'UO',
    label: 'Ducks',
  },
  // The NCAA data lists Texas State's gold as primary; the school leads with maroon.
  'Texas State University': {
    primary: '#501214',
    secondary: '#8D734A',
    monogram: 'TXST',
    label: 'Bobcats',
  },
};

export const DEFAULT_THEME = {
  primary: '#E8602C', // accent.DEFAULT; the old off-system #F4663E is retired
  secondary: '#1B2432',
  monogram: 'UF',
  label: 'UniFreelancer',
};

const SKIP_WORDS = new Set(['of', 'the', 'and', 'at', '-']);

function monogramFromName(name) {
  const letters = name
    .split(/\s+/)
    .filter((word) => word && !SKIP_WORDS.has(word.toLowerCase()))
    .map((word) => word[0].toUpperCase())
    .join('');
  return (letters || name.slice(0, 3).toUpperCase()).slice(0, 3);
}

// Same rolling-hash approach as HireTalent.jsx's hashId, so a given school
// name always produces the same generated color pair.
function hashString(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
}

function generatedTheme(university) {
  const hash = hashString(university);
  const hue = hash % 360;
  return {
    primary: `hsl(${hue}, 65%, 45%)`,
    secondary: `hsl(${(hue + 40) % 360}, 55%, 30%)`,
    monogram: monogramFromName(university),
    label: university,
  };
}

export function getUniversityTheme(university) {
  if (!university) return DEFAULT_THEME;
  if (UNIVERSITY_THEMES[university]) return UNIVERSITY_THEMES[university];
  if (ncaaSchoolColors[university]) {
    return {
      primary: ncaaSchoolColors[university].primary,
      secondary: ncaaSchoolColors[university].secondary,
      monogram: monogramFromName(university),
      label: university,
    };
  }
  return generatedTheme(university);
}

/* ─── Wearing a school's colours legibly ───────────────────────────────────
   School colours are not a palette we control: they run from near-black navy
   to Oregon State orange to a pale gold nobody can read on paper. Anywhere a
   school's own colour carries text, it has to be darkened until it clears
   WCAG AA against the surface it sits on, rather than trusted as-is. Oregon
   State's #DC4405 is 4.3:1 on white, so even a flagship brand colour misses.

   Both shapes `getUniversityTheme` can return are handled: hex from the NCAA
   table and the pinned themes, `hsl(...)` from the generated fallback. */

function parseColor(color = '') {
  const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1];
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  }
  const hsl = color.trim().match(/^hsl\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*\)$/i);
  if (hsl) return hslToRgb(Number(hsl[1]), Number(hsl[2]) / 100, Number(hsl[3]) / 100);
  return [107, 98, 82]; // press.muted, so an unparseable colour still reads
}

function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return [r, g, b].map((v) => Math.round((v + m) * 255));
}

function luminance([r, g, b]) {
  const ch = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

function contrast(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));

// Scale toward black until the ratio is met. Steps of 5% keep the hue
// recognisable as the school's; pure black is the floor for the few colours
// that never get there (it never happens for a real brand colour).
function darkenUntil(rgb, bg, ratio) {
  let out = rgb;
  for (let k = 1; k >= 0 && contrast(out, bg) < ratio; k -= 0.05) {
    out = mix([0, 0, 0], rgb, Math.max(k, 0));
  }
  return out;
}

const css = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;

/* The pieces a school-coloured badge needs, all guaranteed legible:
   `tint` for the badge ground, `ink` for its text and icon at AA (4.5:1),
   `bar` for a graphic like a progress fill at the 3:1 non-text threshold, and
   `primary` untouched for anywhere the raw brand colour is wanted.
   `surface` is what the badge sits on, white by default (press.card). */
export function universityBadge(university, surface = '#FFFFFF') {
  const { primary } = getUniversityTheme(university);
  const base = parseColor(primary);
  const ground = parseColor(surface);
  const tint = mix(ground, base, 0.14);
  const track = parseColor('#F4F4F5'); // press.paper-2
  return {
    primary,
    tint: css(tint),
    edge: `rgba(${base.join(', ')}, 0.28)`,
    ink: css(darkenUntil(base, tint, 4.5)),
    bar: css(darkenUntil(base, track, 3)),
  };
}
